export const CHAT_CONTENT_MARKER = '\uFFFC';

export type DecodedChatContent =
  | { kind: 'text'; text: any }
  | { kind: 'location'; lat: number; lng: number; label: string; url: string }
  | { kind: 'reply'; replyToId: string | null; senderName: string; snippet: string; body: string };

export interface ChatDisplayFields {
  kind: 'text' | 'location' | 'reply';
  content: any;
  location?: { lat: number; lng: number; label: string; url: string };
  reply?: { replyToId: string | null; senderName: string; snippet: string; body: string };
}

function decodeLocationFromParts(p: string[]): DecodedChatContent | null {
  if (p[1] !== 'loc' || p.length < 6) return null;
  const lat = parseFloat(p[2]);
  const lng = parseFloat(p[3]);
  if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) {
    return null;
  }
  return {
    kind: 'location',
    lat,
    lng,
    label: (p[4] || '').trim(),
    url: (p[5] || '').startsWith('http')
      ? p[5]
      : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
  };
}

function decodeReplyFromParts(p: string[]): DecodedChatContent | null {
  if (p.length < 4) return null;

  const body = p[p.length - 1];
  if (p.length === 5) {
    return { kind: 'reply', replyToId: p[1], senderName: p[2], snippet: p[3], body };
  }
  if (p.length === 4) {
    return { kind: 'reply', replyToId: null, senderName: p[1], snippet: p[2], body };
  }

  // Reply quoting a location share embeds the encoded location as the snippet,
  // which adds extra marker-separated fields beyond the normal 4/5-part reply.
  const locIdx = p.indexOf('loc', 1);
  if (locIdx >= 3 && p[locIdx - 1] === '') {
    const snippet = CHAT_CONTENT_MARKER + p.slice(locIdx, p.length - 1).join(CHAT_CONTENT_MARKER);
    if (locIdx === 4) {
      return { kind: 'reply', replyToId: p[1], senderName: p[2], snippet, body };
    }
    if (locIdx === 3) {
      return { kind: 'reply', replyToId: null, senderName: p[1], snippet, body };
    }
  }

  return {
    kind: 'reply',
    replyToId: p[1],
    senderName: p[2],
    snippet: p.slice(3, p.length - 1).join(CHAT_CONTENT_MARKER),
    body
  };
}

export function decodeChatContent(content: unknown): DecodedChatContent {
  if (typeof content !== 'string' || !content.startsWith(CHAT_CONTENT_MARKER)) {
    return { kind: 'text', text: content };
  }

  const p = content.split(CHAT_CONTENT_MARKER);
  if (p[1] === 'loc') {
    return decodeLocationFromParts(p) || { kind: 'text', text: content };
  }

  return decodeReplyFromParts(p) || { kind: 'text', text: content };
}

export function chatDisplayFields(content: unknown): ChatDisplayFields {
  const decoded = decodeChatContent(content);
  if (decoded.kind === 'location') {
    return {
      kind: 'location',
      content: decoded.label,
      location: { lat: decoded.lat, lng: decoded.lng, label: decoded.label, url: decoded.url }
    };
  }
  if (decoded.kind === 'reply') {
    return {
      kind: 'reply',
      content: decoded.body,
      reply: {
        replyToId: decoded.replyToId,
        senderName: decoded.senderName,
        snippet: previewChatContent(decoded.snippet),
        body: decoded.body
      }
    };
  }
  return { kind: 'text', content: decoded.text };
}

export function previewChatContent(content: unknown): string {
  const decoded = decodeChatContent(content);
  if (decoded.kind === 'location') {
    return (decoded.label || `${decoded.lat}, ${decoded.lng}`).trim();
  }
  if (decoded.kind === 'reply') {
    return decoded.body || '';
  }
  return decoded.text == null ? '' : String(decoded.text);
}

export function previewChatMessage(message: any): string {
  return previewChatContent(message?.content || message?.body || message?.message || '');
}

function chatMessageCreatedMs(message: any): number {
  const created = message?.created ?? message?.createdAt;
  if (!created) return 0;
  if (typeof created?.toDate === 'function') {
    return created.toDate().getTime();
  }
  const ms = new Date(created).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function chatMessageBodyKey(message: any): string {
  const content = message?.originalContent ?? message?.content ?? message?.body ?? message?.message ?? '';
  const fileName = message?.fileName || '';
  const fileUrl = message?.fileurl || message?.fileUrl || '';
  return `${content}|${fileName}|${fileUrl}`;
}

/** True when payload is the push-notification batch echo of an already-synced chat message. */
export function isChatPushBatchEcho(message: any): boolean {
  if (!message) return false;
  return !!(message.notificationGrade || message.cipher_timestamp);
}

/**
 * Detect the same chat line under different ids (NATS sync vs push-batch vs Mongo history).
 */
export function chatMessagesMatch(existing: any, incoming: any, windowMs = 30000): boolean {
  if (!existing || !incoming) return false;

  const existingId = existing?.id ?? existing?._id;
  const incomingId = incoming?.id ?? incoming?._id;
  if (existingId && incomingId && String(existingId) === String(incomingId)) {
    return true;
  }

  const existingClientId = existing?.clientMessageId;
  const incomingClientId = incoming?.clientMessageId;
  if (existingClientId && incomingClientId && String(existingClientId) === String(incomingClientId)) {
    return true;
  }
  if (existingClientId && incomingId && String(existingClientId) === String(incomingId)) {
    return true;
  }
  if (incomingClientId && existingId && String(incomingClientId) === String(existingId)) {
    return true;
  }

  const senderA = existing?.senderId;
  const senderB = incoming?.senderId;
  if (!senderA || !senderB || String(senderA) !== String(senderB)) {
    return false;
  }
  if (chatMessageBodyKey(existing) !== chatMessageBodyKey(incoming)) {
    return false;
  }

  const tA = chatMessageCreatedMs(existing);
  const tB = chatMessageCreatedMs(incoming);
  if (!tA || !tB) {
    return true;
  }
  return Math.abs(tA - tB) <= windowMs;
}

export function resolveChatMessageId(data: any, element?: any): string {
  return (
    data?._id ||
    data?.id ||
    element?.id ||
    element?._id ||
    data?.clientMessageId ||
    `${Date.now()}_${Math.random()}`
  );
}
